/* @flow */
import { onError } from 'apollo-link-error';
import { ApolloLink } from 'apollo-link';

/* Return all the HTTP error codes extracted from the request error.
 * Codes, plural because each graph resolver maybe have
 * have errored diferrently.
 *
 * {
 *   me {
 *     classes    { ... }
 *     phoneCalls { ... }
 *   }
 * }
 *
 * classes might have 500.
 * phoneCalls might have 401.
 *
 */
function extractStatusCodesFromError(error): Set<string> {
  const codes = new Set();

  if (!Array.isArray(error.graphQLErrors)) {
    return codes;
  }

  // extract the code for the service metatdata error
  error.graphQLErrors.forEach(err => {
    if (err.serviceMetadata.service_status) {
      codes.add(err.serviceMetadata.service_status.toString());
    }
  });

  return codes;
}

type Options = {
  status: { [string]: Function },
};

/* QueryErrorLink is an apollo link to respond to graphql errors.
 *
 * const queryErrorLink = QueryErrorLink({
 *   status: {
 *     '401': ()      => {},
 *     '403': ()      => {},
 *     '404': ()      => {},
 *     '503': ()      => {},
 *     '*':   (codes) => {},
 *   },
 * });
 *
 * Multiple handlers may be called for the same query, as
 * a single query resolving may error in different ways.
 * A graphql query may return a 403 and a 503 at the same time.
 *
 * options.status is a map of string <=> function because flow
 * doesn not support numbers as object key value yet.
 */
class QueryErrorLink extends ApolloLink {
  constructor(options: Options) {
    super();
    this.options = options;
    this.link = onError(this.handler);
  }

  handler = (error: *) => {
    const codes = extractStatusCodesFromError(error);

    for (const code of codes) {
      if (this.options.status[code]) {
        this.options.status[code]();
        codes.delete(code);
      }
    }

    if (codes.size > 0 && this.options.status['*']) {
      this.options.status['*'](codes);
    }
  };

  request(operation: *, forward: *) {
    return this.link.request(operation, forward);
  }
}

export { QueryErrorLink };
